import { useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IonList, IonItem, IonLabel, IonInput, IonText } from '@ionic/react';

import { getValue } from 'helpers/events';
import { gteTo } from 'lib/number';

import ProductActions from 'actions/ProductActions';
import { updateLoad } from 'actions/ProjectDataActions';
import { readOnlySelector } from 'selectors/projectSelector';
import { pushPierProductsSelector, ramProductsSelector } from 'selectors/productSelector';

import { ProjectContext } from 'components/layouts/Project';
import SelectInput from 'components/helpers/SelectInput';
import NumberInput from 'components/helpers/number-input';

const AUTO_SELECT = "Auto Select";
const GALVANIZATION_TYPES = ["Non-Galvanized", "Galvanized"];
const maxBlowCountDepth = (blowCounts=[]) => blowCounts.slice(1).indexOf(0) * 5;
const defaultLengthFor = (blowCounts) => Math.max(maxBlowCountDepth(blowCounts), 12);
const productForCapacity = (products, minCapacity) => (
  _.minBy(products.filter((p) => p.capacity >= minCapacity), 'capacity')
) || (
  _.maxBy(products, 'capacity')
);
const ramForDiameter = (rams, diameter) => {
  const minBore = Math.min(..._.map(rams, 'bore_in').filter(gteTo(diameter)));
  return _.filter(rams, {bore_in: minBore})
}

export default function PushPierOptions({load}) {
  const {id:loadId, product, ram, coating, depth, aboveGrade=0, compression=0, tension=0} = load;
  const dispatch = useDispatch();
  const {settings: {lengthUnit} , data: {blowCounts}} = useContext(ProjectContext);
  const readonly = useSelector(readOnlySelector);
  const products = useSelector(pushPierProductsSelector);
  const ramProducts = useSelector(ramProductsSelector);
  const compatableRams = ramForDiameter(ramProducts, product?.diameter);
  const minCapacity = Math.max(tension, compression);

  const update = (...args) => updateLoad(loadId, ...args);

  const setProduct = (newProduct) => {
    dispatch(update({product: newProduct}));

    const compatableRams = ramForDiameter(ramProducts, newProduct.diameter);
    if (!_.includes(compatableRams, ram)) {
      dispatch(update({ram: _.first(compatableRams)}));
    }
  };

  const setProductSku = (sku) => {
    if (sku === AUTO_SELECT) {
      return setProduct(productForCapacity(products, minCapacity));
    }
    return setProduct(_.find(products, {sku}));
  };

  useEffect(() => {
    if (_.isEmpty(products)) ProductActions.getPushPierProducts();
    if (!coating) {
      dispatch(update({coating: "Galvanized"}));
    }
    if (!depth) {
      dispatch(update({depth: defaultLengthFor(blowCounts)}));
    }
    if (!product && _.isPresent(products)) {
      setProductSku(AUTO_SELECT);
    }
  }, [_.isEmpty(products)]);

  const setRam = (sku) => {
    dispatch(update({ram: _.find(ramProducts, {sku})}));
  };

  return (
    <IonList>
      <h3>Push Pier</h3>

      {product?.admin_only && (
        <p className="admin-alert">⚠ You are viewing an admin only product.</p>
      )}
      <SelectInput label="Shaft"
        readonly={readonly}
        value={product?.sku}
        onChange={getValue(setProductSku)}
        options={[AUTO_SELECT, ..._.map(products, 'sku')]}
        allowBlank={false}/>
      <SelectInput label="Coating"
        readonly={readonly}
        value={coating}
        onValueChange={(coating) => dispatch(update({coating}))}
        options={GALVANIZATION_TYPES}
        allowBlank={false}/>
      <SelectInput label="Ram Kit"
        readonly={readonly}
        value={ram?.sku}
        onChange={getValue(setRam)}
        options={_.map(compatableRams, 'sku')}
        allowBlank={false}/>

      <IonItem>
        <IonLabel>Depth</IonLabel>
        <IonInput type="number"
          disabled={readonly}
          value={_.round(lengthUnit?.toDisplay(depth), 1)}
          onIonChange={getValue((value) => {
            dispatch(update({depth: _.round(lengthUnit?.toBase(value))}))
          })}
          min={1}
          max={200}/>
        <IonText slot="end">
          {lengthUnit?.label}
        </IonText>
      </IonItem>
      <NumberInput label={<>Pile Above(+)/<wbr/>Below(-) Grade</>}
        value={_.round(lengthUnit?.toDisplay(aboveGrade), 1)}
        onValueChange={(value) => dispatch(update({aboveGrade: _.round(lengthUnit?.toBase(value))}))}
        unit={lengthUnit}
        className="label-max-width"
        readonly={readonly}/>
    </IonList>
  );
}
